import React, { useEffect } from "react";
import { colorOf } from "@/lib/constants";

/**
 * BirthdayCountdown — fullscreen playful screen for a toddler who just
 * tapped their own birthday card on the calendar-only dashboard.
 *
 * Only rendered for the toddler's own birthday (filtered by Dashboard
 * when isToddlerMode && ev.member_ids.includes(toddlerId)) AND when
 * the birthday is within the next 7 days. The close button and any
 * tap on the backdrop exits — no other controls (matches the toddler
 * ethos of "one thing at a time").
 *
 * `daysUntil` 0..7 controls which countdown message shows.
 */
const CONFETTI = ["🎉", "🎊", "🎈", "🎁", "🧁", "🍰", "⭐️", "✨", "🎂", "🪅"];

export default function BirthdayCountdown({ member, age, daysUntil, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Auto-dismiss after 10s — toddler mode rule: one thing at a time,
  // no lingering UI. Tapping the backdrop or pressing ESC still
  // closes early.
  useEffect(() => {
    const t = setTimeout(() => onClose && onClose(), 10000);
    return () => clearTimeout(t);
  }, [onClose]);

  const clamped = Math.max(0, Math.min(7, daysUntil | 0));
  const color = colorOf(member.color);

  // Build 28 confetti pieces with randomised position, delay and size.
  // Seeded per render — the animation loops so it always feels alive.
  const pieces = React.useMemo(() => {
    const arr = [];
    for (let i = 0; i < 28; i++) {
      const emoji = CONFETTI[i % CONFETTI.length];
      const left = Math.round(Math.random() * 100);
      const delay = Math.round(Math.random() * 3000);
      const duration = 4000 + Math.round(Math.random() * 3000);
      const size = 28 + Math.round(Math.random() * 40);
      const rotate = Math.round(Math.random() * 720 - 360);
      arr.push({ emoji, left, delay, duration, size, rotate, key: `c${i}` });
    }
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.id]);

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center overflow-hidden"
      style={{ background: `color-mix(in srgb, ${color} 35%, var(--bg))` }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Birthday countdown for ${member.name}`}
      data-testid={`bday-countdown-${member.id}`}
    >
      {/* Falling confetti layer */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {pieces.map((p) => (
          <span
            key={p.key}
            className="absolute ff-bday-confetti-piece"
            style={{
              left: `${p.left}%`,
              top: "-10vh",
              fontSize: `${p.size}px`,
              animationDelay: `${p.delay}ms`,
              animationDuration: `${p.duration}ms`,
              ["--spin"]: `${p.rotate}deg`,
            }}
          >
            {p.emoji}
          </span>
        ))}
      </div>

      {/* Centre stack: a single big "Turning N in X days" pill. Clean
          readable anchor for toddler eyes among the confetti. On the
          actual birthday (daysUntil === 0) it reads "Happy birthday!"
          instead. */}
      <div
        className="relative z-[1] flex flex-col items-center text-center px-6 max-w-xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="inline-flex items-center gap-3 md:gap-4 px-7 py-5 md:px-10 md:py-7 rounded-full bg-[color:var(--surface)] font-display-italic text-4xl md:text-6xl leading-[0.95] tracking-tight ff-bday-bounce"
          data-testid={`bday-countdown-age-${member.id}`}
        >
          <span aria-hidden="true">🎂</span>
          <span>
            {clamped === 0
              ? "Happy birthday!"
              : age != null
                ? `Turning ${age} in ${clamped} ${clamped === 1 ? "sleep" : "sleeps"}`
                : `${clamped} ${clamped === 1 ? "sleep" : "sleeps"} to go`}
          </span>
        </div>
      </div>

      {/* Keyframes live here so this component is fully self-contained */}
      <style>{`
        @keyframes ff-bday-fall {
          0% { transform: translate3d(0, 0, 0) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translate3d(-20px, 115vh, 0) rotate(var(--spin, 360deg)); opacity: 1; }
        }
        .ff-bday-confetti-piece {
          animation-name: ff-bday-fall;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
          will-change: transform, opacity;
        }
        @keyframes ff-bday-bounce-anim {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50% { transform: translate3d(0, -10px, 0) scale(1.02); }
        }
        .ff-bday-bounce {
          animation: ff-bday-bounce-anim 2.4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
